/***********************/
/* Angular + external */
import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ElementRef,
  Renderer2,
  ViewChildren,
  QueryList,
  ChangeDetectorRef,
  Host,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";

import { Observable, ReplaySubject, Subject, Subscription, take } from "rxjs";

/***********************/
/* BFX */
import { GoldenLayoutPopout } from "../../golden-layout/golden-layout-popout";
import { GoldenLayoutComponent } from "../../golden-layout/golden-layout-component";
import { GoldenLayoutService } from "../../golden-layout/golden-layout-service";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { LogoutNotificationService } from "../../bfx-auth/logout-notification.service";
import { OENDisplayState } from "../data/enums/oe-display-state.enum";
import { OENOperationMode } from "../data/enums/oe-operation-mode.enum";
import { OENExternalCommand } from "../data/enums/oe-external-command.enum";
import { OENCommand } from "../data/enums/oe-command.enum";
import { OECommandEventModel } from "../data/models/oe-command-event.model";
import { OECoordinatorService } from "../services/oe-coordinator.service";
import { OEApiService } from "../services/oe-api.service";
import { OEUtilityService } from "../services/basic/oe-utility.service";
import { OENEntryType } from "../data/enums/oe-entry-type.enum";
import { OEActionsPanelComponent } from "./oe-panel-actions.component";
import { OEStrategyPanelComponent } from "./oe-panel-strategy.component";
import { OEGammaViewerComponent } from "./oe-panel-gamma-viewer.component";
import { OECustomisePanelComponent } from "./oe-panel-customise.component";
import { OEScreenContainerClickListenerService } from "../services/oe-container-click-listener.service";
import { NativeDesktopWindow } from "shared/native-desktop/NativeDesktopWindow";
import { OEModelGetterService } from "app/order-entry/services/model/oe-model-getter.service";
import { IActionCallout, IEMActionCallout } from "app/bfx-element-library/models/CalloutModel";
import { OEUpdateMonitorService } from "app/order-entry/services/oe-update-monitor.service";
import { OrderState, RequestOrderType } from "shared/cubiq-model-multi-asset";

@Component({
  selector: "bfx-order-entry-screen",
  templateUrl: "./html/oe-screen-container.html",
  providers: [OEScreenContainerClickListenerService],
  standalone: false,
})
export class OEScreenContainerComponent
  implements GoldenLayoutComponent, OnDestroy, OnInit, AfterViewInit
{
  @ViewChild("orderEntryView") orderEntryView: ElementRef;

  @ViewChild("strategyView") strategyView: ElementRef; // container
  @ViewChild("panelStrategy") panelStrategy: OEStrategyPanelComponent;

  @ViewChild("gammaLegViewer")
  gammaLegViewer: OEGammaViewerComponent; ///de gammas
  @ViewChild("panelCustomise")
  panelCustomise: OECustomisePanelComponent;
  @ViewChild("gammaContainer")
  gammaContainer: ElementRef;

  @ViewChildren("panelSpot") panelSpot: QueryList<any>;
  @ViewChildren("panelAlgo") panelAlgo: QueryList<any>;
  @ViewChildren("panelNdf") panelNdf: QueryList<any>;
  @ViewChildren("panelBmark") panelBmarkEntry: QueryList<any>;
  @ViewChildren("panelActions") panelActions: OEActionsPanelComponent;
  @ViewChildren("panelGamma") panelGamma: QueryList<any>;

  public scratchpad: boolean = false;

  /***************************************/
  /* DATA */

  private parentPopoutSubject: Subject<GoldenLayoutPopout> = new ReplaySubject(1);
  public parentPopout: GoldenLayoutPopout;
  public parentPopout$: Observable<GoldenLayoutPopout> = this.parentPopoutSubject.asObservable();

  public refId: string = null; // active order
  public rootRefId: string = null; // refId of the root order
  public refIds: Array<string> = new Array<string>(); // list of orders' refIds this component listens to;

  public operationMode: OENOperationMode = null;
  public entryType: OENEntryType = null;

  /** Bound property for template Execution Monitor - when defined, the monitor is initialised and visible  */
  public executionOrderId: string;
  /** Execution monitoring enabled */
  public executionMonitoringEnabled: boolean = true;

  public viewInitComplete: boolean = false;

  public control = {
    autoClose: false,
    logoutCleanup: false,
    strategyLayout: null,
    activeTabIndex: 0,
    initializeUserDefaults: true,
    panelLoadCompleted: false,
    displayState: null,
    panels: {
      spot: {
        show: false,
        initialized: false,
      },
      algo: {
        show: false,
        initialized: false,
      },
      ndfAlgo: {
        show: false,
        initialized: false,
      },
      ndf: {
        show: false,
        initialized: false,
      },
      bmark: {
        show: false,
        initialized: false,
      },
      gamma: {
        show: false,
        initialized: false,
      },
      strategyLayout: {
        show: false,
        height: 500,
      },
      gammaLayout: {
        show: false,
        height: 500,
      },
      notifications: {
        show: false,
        showButtons: true,
      },
      actions: {
        show: false,
      },
      debug: {
        show: false,
      },
    },
  };

  private layoutSubscription: Subscription;
  private coordinatorSubscription: Subscription;
  private desktopWindow: NativeDesktopWindow;

  // @ViewChild('tabs') elTabs: any;

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */

  constructor(
    public element: ElementRef,
    public translate: TranslateService,
    private coordinator: OECoordinatorService,
    private api: OEApiService,
    private utility: OEUtilityService,
    private getter: OEModelGetterService,
    private _layoutService: GoldenLayoutService,
    public renderer2: Renderer2,
    private _logoutNotificationService: LogoutNotificationService,
    public cd: ChangeDetectorRef,
    @Host() public clickListener: OEScreenContainerClickListenerService,
    private updateMonitor: OEUpdateMonitorService,
  ) {
    this.initialize();
  }

  /**
   * These @ HostListeners  are not working on Desktop (it attaches on main body and works one to many)
   * The keyboard events are defined bellow (commented) are replaced by attachOrderEntryKeyUpEvents() in GoldenLayoutPopoutContainer
   */
  /*
    @HostListener('document:keydown.tab', ['$event'])
	public adjustStrategyHeight(event: KeyboardEvent) {
		// check if tab is pressed
		if (event.keyCode === 9) {
			this.updateStrategyPanelSize(null);
		}
	}

    // submit on ctrl+enter as per https://www.bcdfx.com/ijira/browse/OMS-29184
    @HostListener('document:keydown.control.enter', ['$event'])
    public onSubmitKeys($event: KeyboardEvent) {
        if ($event.ctrlKey && $event.keyCode == 13) {
            this.panelActions.submit();
        }
    }
     */

  ngOnDestroy() {
    //   this.configurationSubscription.unsubscribe();
    this.viewInitComplete = false;
    this.layoutSubscription.unsubscribe();
    this.coordinatorSubscription.unsubscribe();
    this.api.screenUnregister(this.rootRefId);
  }

  ngOnInit() {
    this.api.screenRegister(this.rootRefId);
  }

  ngAfterViewInit() {
    //  this.makeTabsUntabbable();
    if (this.control.initializeUserDefaults) {
      this.loadOrderEntryDefaults();
    }
    if (this.control.panels.gamma.show) {
      this.parentPopout$.pipe(take(1)).subscribe((parentPopout) => {
        if (parentPopout != null) {
          parentPopout.addContainerClass("has-strategy");
        }
      });
    }

    if (this.control.panels.algo.show || this.control.panels.ndfAlgo.show) {
      this.parentPopout$.pipe(take(1)).subscribe((parentPopout) => {
        if (parentPopout != null) {
          parentPopout.addContainerClass("has-algo");
        }
      });
    }

    this.updatePopupTitle(this.refId);
    this.viewInitComplete = true;
  }

  /**********************************************************************/
  /* INITIALIZATION */

  protected initialize(): void {
    this.subscribeToCoordinator();
    this.subscribeToLayout();
    // this.updateExtendedWorkerLogSate();
  }

  /**
   * @param data properties:
   *  - refId: reference id for BfxOrderEntryData, default null
   *  - id (optional): order id for OrderDataImpl: default null
   *  - loadDefaults: boolean indicating whether the order entry defaults should be loaded, default false
   *  - mode: OENOperationMode indicating the initialization mode for the screen
   *  - entryType: OENEntryType
   */
  glInit(data: any, container: any, state: any): void {
    let errorsFg = false;
    let refId = data != null && data.refId != null ? data.refId : null;
    let mode = data != null && data.mode != null ? data.mode : null;

    if (mode == null || Object.values(OENOperationMode).includes(mode) == false) {
      errorsFg = true;
    } else if (
      mode !== OENOperationMode.NEW &&
      (refId == null || (refId != null && this.api.has(refId) === false))
    ) {
      errorsFg = true;
    } else {
      // do all validation requiring closing of OE _before_ else branch.
      this.operationMode = data.mode;
      this.entryType = data.entryType;
      this.activateEntryPanel();
      this.activateActions();

      errorsFg = this.setupFromOperationMode(data, refId);
    }

    if (errorsFg) {
      logger.out.error("ERROR: _oeScreen.glInit(): closing order entry due to data errors");
      container["_config"].glInitError = Error("data for Order Entry");
    }

    // applies show configuration
    this.cd.detectChanges();
  }

  private setupFromOperationMode(data: any, refId: any) {
    let errorsFg: boolean;
    switch (this.operationMode) {
      case OENOperationMode.NEW:
        refId = this.api.actionCreateOrder(this.entryType);
        this.setRootReferenceId(refId);
        if (data.loadDefaults != null && typeof data.loadDefaults === "boolean") {
          this.control.initializeUserDefaults = data.loadDefaults;
        }
        // if no data loading, mark as loaded
        if (!this.control.initializeUserDefaults) {
          this.handlePanelLoadCompleted(true);
        }
        break;
      case OENOperationMode.DUPLICATE:
      case OENOperationMode.EDIT:
      case OENOperationMode.AMEND_MARKUP:
        if (refId != null) {
          this.setRefsAndActivateListenersOnInit(data.rootRefId, refId);
          this.control.initializeUserDefaults = false;
          // this.scratchpad = data.scratchpad;
          if (data.extLoad == null || data.extLoad !== false) {
            setTimeout(() => {
              this.api.actionLoadOrderOnInit(refId);
            }, 300);
          }
        }
        break;
      case OENOperationMode.MODIFY:
      case OENOperationMode.MODIFY_INFO:
      case OENOperationMode.MONITOR_EXECUTION:
      case OENOperationMode.MODIFY_SCRATCHPAD:
        if (refId != null) {
          this.setRefsAndActivateListenersOnInit(data.rootRefId, refId);
          this.scratchpad = data.scratchpad;
          this.initializeLoadOperation(refId);
        }
        break;
      default:
        errorsFg = true;
        break;
    }
    return errorsFg;
  }

  /**
   * Handle initial setting and subsequent modification of operation mode. This will kick off the data load operation for
   * the current entity
   *
   * @param refId
   */
  private initializeLoadOperation(refId: any) {
    switch (this.operationMode) {
      case OENOperationMode.MODIFY:
      case OENOperationMode.MODIFY_INFO:
      case OENOperationMode.MODIFY_SCRATCHPAD:
      case OENOperationMode.MONITOR_EXECUTION:
        // Ensure that user defaults are not set on the model
        this.control.initializeUserDefaults = false;
        setTimeout(() => {
          // Initialise loading operations of data into their respective controls
          this.api.actionLoadOrderOnInit(
            refId,
            this.operationMode === OENOperationMode.MODIFY,
            this.control.displayState,
          );
          this.initActiveTab();
        }, 300);
        break;
      default:
        break;
    }
  }

  /**
   * Set the active tab based on the current active operation - MODIFY_INFO should always
   * show the second tab
   */
  private initActiveTab() {
    if (this.operationMode === OENOperationMode.MODIFY_INFO) {
      this.control.activeTabIndex = 1;
    } else {
      this.control.activeTabIndex = 0;
    }
  }

  public handlePanelLoadCompleted($event: any): void {
    //logger.out.warn(`OE Container -> operation ${OENOperationMode[this.operationMode]} | alreadyLoaded: ${this.control.panelLoadCompleted ? 'NO' : 'YES'} | event: InitLoadComplete ${$event}`);
    if (!this.control.panelLoadCompleted && typeof $event === "boolean" && $event) {
      setTimeout(() => {
        if (this.control.displayState == null) {
          switch (this.operationMode) {
            case OENOperationMode.NEW:
            case OENOperationMode.MODIFY:
            case OENOperationMode.EDIT:
            case OENOperationMode.DUPLICATE:
            case OENOperationMode.MODIFY_SCRATCHPAD:
            case OENOperationMode.MODIFY_INFO:
              this.coordinator.cmdChangeDisplayState(this.refId, OENCommand.STATE_ORDER_ENTRY);
              //logger.out.warn(`OE Container -> issue state change ${OENCommand[OENCommand.STATE_ORDER_ENTRY]}`);
              break;
            case OENOperationMode.MONITOR_EXECUTION:
              this.coordinator.cmdMonitorExecution(this.refId);
              break;
            case OENOperationMode.AMEND_MARKUP:
              break;
          }
        } else {
          //logger.out.warn(`OE Container -> No state change | current state ${OENCommand[this.control.displayState]}`);
        }
        this.initActiveTab();
        this.control.panelLoadCompleted = true;
      }, 150);
    } else {
      //logger.out.warn(`OE Container -> No state change`);
    }
  }

  public handlePanelTabChange(tabIndex: any): void {
    // need to store this - so there is a change in value when it changes - or else it will not be propagated
    this.control.activeTabIndex = tabIndex;
  }
  /**********************************************************************/
  /* SUBSCRIPTIONS */

  private subscribeToLayout(): void {
    // show debug panel
    this.layoutSubscription = this._layoutService.orderEntryCmd$.subscribe(
      (event: { cmd: OENExternalCommand; data: any }) => {
        if (event.cmd === OENExternalCommand.OPEN_DEBUG_PANEL && this.rootRefId === event.data)
          this.control.panels.debug.show = !this.control.panels.debug.show;
      },
    );
    // handling logout
    this._logoutNotificationService.getLogoutNotification().subscribe(
      (v) => {
        try {
          this.control.logoutCleanup = true;
          this.api.actionExtendedDestroyOnClose(this.refId);
        } catch (ex) {}
        // don't suppress logout!
        v.res(true);
      },
      () => {},
      () => {},
    );
  }

  private subscribeToCoordinator(): void {
    this.coordinatorSubscription = this.coordinator.orderEntryScrCmd$.subscribe(
      ($event: OECommandEventModel) => {
        if ($event != null && ($event.ref === this.refId || $event.ref === this.rootRefId)) {
          switch ($event.cmd) {
            // refId management
            case OENCommand.REF_ACTIVATE:
              this.refId = $event.ref;
              this.updatePopupTitle($event.ref);
              this.updateExecutionMonitorVisibility();
              break;
            case OENCommand.REF_ADD:
              this.handleAddRefIdToListeners($event.data);
              break;
            case OENCommand.REF_REMOVE:
              this.handleRemoveRefIdFromListeners($event.data);
              break;
            case OENCommand.REF_REMOVE_ROOT:
              this.handleRemoveRefIdFromListeners($event.data, true);
              break;
            // new order model
            case OENCommand.NEW_MODEL:
              this.handleNewModel($event.data);
              break;
            case OENCommand.LOAD_DEFAULTS:
              this.loadOrderEntryDefaults();
              break;
            // panel - client details
            case OENCommand.SHOW_TAB_FINANCIALS:
              this.changeTab(0);
              break;
            case OENCommand.SHOW_TAB_MORE:
              this.changeTab(1);
              break;
            // panel - strategy layout
            case OENCommand.PANEL_SHOW_STRATEGY:
              this.showStrategyLayout();
              break;
            case OENCommand.PANEL_HIDE_STRATEGY:
              this.hideStrategyLayout();
              break;
            // panel - notifications
            case OENCommand.PANEL_SHOW_NOTIFICATIONS:
              this.control.panels.notifications.show = true;
              break;
            case OENCommand.PANEL_HIDE_NOTIFICATIONS:
              this.control.panels.notifications.show = false;
              break;
            // panel - actions
            case OENCommand.PANEL_SHOW_ACTIONS:
              this.control.panels.actions.show = true;
              break;
            case OENCommand.PANEL_HIDE_ACTIONS:
              this.control.panels.actions.show = false;
              break;

            case OENCommand.LOGOUT_CLEANUP_ORDER_ENTRY:
              if ($event.data != null && typeof $event.data === "boolean") {
                this.control.autoClose = $event.data;
              } else {
                this.control.autoClose = true;
              }
              this.close();
              break;
            case OENCommand.FOCUS_ORDER_ENTRY:
              if (this.parentPopout != null) {
                this.parentPopout.requestFocus();
              }
              break;
            case OENCommand.RESET_ALL:
              if (this.control.panels.strategyLayout.show) {
                this.hideStrategyLayout();
              }
              break;
            case OENCommand.FREEZE:
              this.parentPopout.disableAllActions = true;
              break;
            case OENCommand.UNFREEZE:
              this.parentPopout.disableAllActions = false;
              break;
            case OENCommand.STATE_ORDER_ENTRY:
            case OENCommand.STATE_ORDER_IN_MODIFICATION:
            case OENCommand.STATE_ORDER_UNLOCKED:
            case OENCommand.STATE_ORDER_PROCESSING:
            case OENCommand.STATE_ORDER_ENTRY_NEGATIVE_MARKUP:
            case OENCommand.BM_ZERO_AMOUNT_WARN:
            case OENCommand.STATE_ORDER_ENTRY_TOLERANCE_ERROR:
            case OENCommand.STATE_ORDER_ENTRY_TOLERANCE_WARN:
            case OENCommand.STATE_ORDER_SUCCESS:
            case OENCommand.STATE_ORDER_REJECTED:
            case OENCommand.STATE_ORDER_REJECTED_HCO_ERROR:
            case OENCommand.STATE_ORDER_INT_ERROR:
            case OENCommand.STATE_ORDER_ACCEPTANCE_ALLOWED:
            case OENCommand.STATE_ORDER_ACCEPTANCE_CALL:
            case OENCommand.STATE_ORDER_ACCEPTANCE_EXTERNAL:
            case OENCommand.STATE_ORDER_MODIFY_OUT_ACCEPTANCE:
            case OENCommand.STATE_ORDER_EXT_MODIFY_OUT_ACCEPTANCE:
            case OENCommand.STATE_ORDER_APPROVAL_ALLOWED:
            case OENCommand.STATE_ORDER_APPROVAL_INTERNAL:
            case OENCommand.STATE_ORDER_APPROVAL_EXTERNAL:
            case OENCommand.STATE_ORDER_AMEND_MARKUP:
            case OENCommand.STATE_MONITOR_EXECUTION:
            case OENCommand.STATE_ORDER_MODIFICATION_EXTERNAL:
            case OENCommand.STATE_ORDER_EXPIRY_PENDING:
              // Ensure operation is consistent with montoring state for title purposes
              if ($event.cmd == OENCommand.STATE_MONITOR_EXECUTION) {
                this.operationMode = OENOperationMode.MONITOR_EXECUTION;
                // Need to ensure that we are upto date with all out leg references - after an Abort/reload thiese need to be refreshed
                this.refIds = this.getter.getStrategyNodesListRoot(this.refId);
              }

              this.control.displayState = this.api.transformCmdToDisplayState($event.cmd);
              this.api.screenUpdateDisplay(this.refId, this.control.displayState);
              this.updatePopupTitle(this.refId);
              this.updateExecutionMonitorVisibility();

              setTimeout(() => {
                if (
                  this.entryType === OENEntryType.ALGO ||
                  this.entryType === OENEntryType.NDF_ALGO
                ) {
                  this.determineNotificationsPanelVisibility();
                }
              }, 50);
              break;
            case OENCommand.UNBULK:
            case OENCommand.BULKED:
            case OENCommand.BULKING:
              break;
            case OENCommand.MODIFY_MONITORED_ORDER:
              const newMode: OENOperationMode = $event.data;
              // Operation MODIFY_MONITORED_ORDER can also be used for triggering reload of data
              this.operationMode = newMode;
              this.initializeLoadOperation(this.refId);
              break;
            case OENCommand.STATE_ORDER_LOST_HIDDEN:
              this.executionMonitoringEnabled = false;
              break;
            default:
              logger.out.error(
                `ERROR: oeScreen.subscribeToCoordinator(): Command not implemented! command= (${
                  $event.cmd
                }) [${$event.cmd != null ? OENCommand[$event.cmd] : "NULL"}]`,
              );
              break;
          }
        } else if (this.refIds.includes($event.ref) && $event.cmd === OENCommand.REF_ACTIVATE) {
          // Activate RefId listeners
          this.refId = $event.ref;
          this.updatePopupTitle($event.ref);
          this.updateExecutionMonitorVisibility();
        } else if (
          this.refIds.includes($event.ref) &&
          $event.cmd === OENCommand.LOGOUT_CLEANUP_ORDER_ENTRY
        ) {
          this.control.autoClose = true;
          this.close();
        }
      },
    );
  }
  changeTab(tabNumber: number) {
    setTimeout(() => {
      this.control.activeTabIndex = tabNumber;
    }, 30);
  }

  private activateActions() {
    this.control.panels.actions.show = true;
  }

  private activateEntryPanel(_entryType?: OENEntryType): void {
    let entryType = _entryType ?? this.entryType;
    if (entryType != null) {
      this.control.panels.spot.show = false;
      this.control.panels.algo.show = false;
      this.control.panels.ndfAlgo.show = false;
      this.control.panels.ndf.show = false;
      this.control.panels.bmark.show = false;
      this.control.panels.gamma.show = false;

      switch (entryType) {
        case OENEntryType.SPOT:
          this.control.panels.spot.show = true;
          break;
        case OENEntryType.ALGO:
          this.control.panels.algo.show = true;
          break;
        case OENEntryType.NDF_ALGO:
          this.control.panels.ndfAlgo.show = true;
          break;
        case OENEntryType.NDF:
          this.control.panels.ndf.show = true;
          break;
        case OENEntryType.BENCHMARK:
          this.control.panels.bmark.show = true;
          break;
        case OENEntryType.GAMMA:
          this.control.panels.gamma.show = true;
          break;
        default:
          logger.out.error(`ERROR: oeScreen.activateEntryPanel(): Unknown entry type ${entryType}`);
          break;
      }
      this.determineNotificationsPanelVisibility(entryType);
    }
  }

  private determineNotificationsPanelVisibility(_entryType?: OENEntryType): void {
    let entryType = _entryType != null ? _entryType : this.entryType;
    if (entryType != null) {
      switch (entryType) {
        case OENEntryType.GAMMA:
        case OENEntryType.SPOT:
        case OENEntryType.NDF:
          this.control.panels.notifications.show = true;
          break;
        case OENEntryType.ALGO:
        case OENEntryType.NDF_ALGO:
          this.control.panels.notifications.show =
            !(
              this.control.displayState == null ||
              this.control.displayState === OENDisplayState.SHOW_ORDER_ENTRY
            ) ||
            (this.control.displayState === OENDisplayState.SHOW_ORDER_ENTRY &&
              this.rootRefId != null &&
              !this.api.getPermissionEnterOrder(this.rootRefId));
          break;
        case OENEntryType.BENCHMARK:
          this.control.panels.notifications.show = true;
          break;
        default:
          logger.out.error(
            `ERROR: oeScreen.determineNotificationsPanelVisibility(): Unknown entry type ${entryType}`,
          );
          break;
      }
    }
  }

  /**
   * We only want the Execution Monitor to be visible in certain modes(when unset, the
   * monitor is not shown) - this will define the monitored ID when order already exists
   * and the Id is not yet defined. Once defined, it cannot be changed or unset
   */
  private updateExecutionMonitorVisibility(): void {
    const newExecutionId = this.api.getOrderIdForRefId(this.refId);
    if (this.getter.supportsMonitoringExecution(this.refId)) {
      const oldExecutionId = this.executionOrderId;
      const strategyRef = this.getter.getStrategyRef(this.refId);
      if (oldExecutionId !== newExecutionId || !this.executionMonitoringEnabled) {
        switch (this.operationMode) {
          case OENOperationMode.NEW:
          case OENOperationMode.DUPLICATE:
          case OENOperationMode.EDIT:
          case OENOperationMode.MODIFY_SCRATCHPAD:
            this.executionOrderId = null;
            this.updateMonitor.unsubscribeStrategyEvents(strategyRef);
            break;
          case OENOperationMode.MODIFY:
          case OENOperationMode.MODIFY_INFO:
          case OENOperationMode.MONITOR_EXECUTION:
          case OENOperationMode.AMEND_MARKUP:
            this.executionMonitoringEnabled = true;
            this.executionOrderId = newExecutionId;
            this.updateMonitor.subscribeStrategyEvents(strategyRef);
            break;
        }
      }
    } else {
      // For Limit orders we still want to see how much has been filled so we show a disabled
      // Execution monitor
      this.executionMonitoringEnabled = false;
      if (this.operationMode === OENOperationMode.MODIFY) {
        const entryType = this.getter.getEntryType(this.refId);
        const isCall = this.getter.get(this.refId)?.orderType?.value === RequestOrderType.CALL;
        switch (entryType) {
          case OENEntryType.NDF:
          case OENEntryType.SPOT:
            this.executionOrderId =
              this.getter.getSrvOrderState(this.refId) === OrderState.IN_MODIFICATION && !isCall
                ? newExecutionId
                : null;
            break;
          default:
            this.executionOrderId = null;
            break;
        }
      }
    }
  }

  private setRefsAndActivateListenersOnInit(rootRefId: string, refId: string): void {
    if (rootRefId != null && this.api.has(rootRefId)) {
      this.setRootReferenceId(rootRefId);
      this.handleAddRefIdToListeners(refId); // add refId listener
      this.refId = refId; // activate that listener
    } else {
      // fallback
      this.setRootReferenceId(refId);
    }
  }

  private setRootReferenceId(refId: string): void {
    if (refId) {
      this.refId = refId;
      this.rootRefId = refId;
    }
    if (!this.refIds.includes(refId)) {
      this.refIds.push(refId);
    }
  }

  private loadOrderEntryDefaults(): void {
    this.api.loadUserDefaults(this.refId);
  }

  private handleRemoveRefIdFromListeners(refId: string, overrideCheck: boolean = false): void {
    if (this.rootRefId !== refId || overrideCheck) {
      if (refId != null && this.refIds.includes(refId)) {
        // delete refId from the listeners array
        let index: number = this.refIds.indexOf(refId);
        if (index >= 0) {
          this.refIds.splice(index, 1);
        }
        if (this.refId === refId) {
          this.refId = this.rootRefId;
        }
      }
    } else {
      logger.out.warn(
        "WARNING: handleRemoveRefIdFromListeners(): Not allowed for root listener! refId='" +
          refId +
          "' rootRefId",
      );
    }
  }
  private handleAddRefIdToListeners(refId: string): void {
    if (refId != null && !this.refIds.includes(refId)) {
      this.refIds.push(refId);
    }
  }

  private handleNewModel(refId: string): void {
    if (
      refId != null &&
      typeof refId === "string" &&
      (refId.length === 16 || refId.length === 36)
    ) {
      this.api.screenPreregister(this.refId, refId);
      this.refId = refId;
      this.rootRefId = refId;
      // register new root in the container
      this.parentPopout.setRootRefId(this.rootRefId);
      // reinitialize listeners array
      this.refIds = new Array<string>();
      this.refIds.push(refId);
      // update title
      this.updatePopupTitle(refId);
    } else {
      logger.out.error(
        "ERROR: BfxOrderEntryScreenComponent.subscribeToCoordinator(): Invalid refID: '" +
          refId +
          "'",
      );
    }
  }

  /**********************************************************************/
  /* GOLDEN LAYOUT */

  setParentPopup(parentPopup: GoldenLayoutPopout) {
    this.parentPopout = parentPopup;
    this.parentPopout.setShowDock(false);
    this.parentPopout.setShowSetings(true);
    this.parentPopout.setShowDebug(this.api.getDebugBtnState());
    this.parentPopout.setRootRefId(this.rootRefId);
    this.parentPopoutSubject.next(this.parentPopout);
  }

  close() {
    // only invoke close here, add code in onClose()
    this.parentPopout.close();
  }

  resize(): void {
    this.updateStrategyPanelSize();
  }

  resizeDocked(event: any) {}

  onDesktopOpen(wnd: NativeDesktopWindow): void {
    this.desktopWindow = wnd;
    wnd.onClose = this.onDesktopClose.bind(this);
  }

  private onDesktopClose(): void {
    let result = this.onClose();
    if (result === true) {
      this.desktopWindow.close();
    } else {
      this.desktopWindow.focus();
    }
  }

  // return false - cancels the popout closing
  onClose(): boolean {
    this._layoutService["order_entry_dialog"] =
      this.parentPopout.popoutContainer.element.nativeElement.ownerDocument.body;

    try {
      if (this.control.logoutCleanup) {
        return true;
      }
      if (this.parentPopout.disableAllActions) {
        return false;
      }

      if (this.control.autoClose || this.parentPopout.closeFromLogout) {
        this.control.autoClose = false;
        if (this.api.getOperationMode(this.refId) === OENOperationMode.MODIFY) {
          this.api.actionExtendedDestroyOnClose(this.refId); //  aborts then deletes
        } else {
          this.api.actionDestroyOnClose(this.refId); // just deletes
        }
        return true;
      } else {
        if (this.api.isOperationModeMonitorExecution(this.refId)) {
          this.api.actionDestroyOnClose(this.refId); // just deletes
          return true;
        } else if (this.api.isOperationModeModify(this.refId)) {
          // MODIFY ORDER
          /* The noDialog variable is true if dialog is not shown (based on state and system.features.enableAbortOnCloseFg) */
          let noDialog = this.api.actionAbortModifyWithDialog(this.refId);
          if (noDialog) {
            // close with error handling
            this.api.actionExtendedDestroyOnClose(this.refId);
          }
          return noDialog;
        } else if (this.api.isOperationModeAmendMarkup(this.refId)) {
          // AMEND MARKUP
          let noDialog = this.api.actionAbortAmendMarkupWithDialog(this.refId);
          if (noDialog) {
            // close with error handling
            this.api.actionExtendedDestroyOnClose(this.refId);
          }
          return noDialog;
        } else {
          // ENTER NEW ORDER & DUPLICATE & MODIFY INFO
          let noDialog = this.api.actionCloseNewOrderEntryWithDialog(this.refId);
          if (noDialog) {
            // close with error handling
            if (this.api.isDisplayState_ShowOrderEntry(this.refId)) {
              this.api.actionDestroyOnClose(this.refId); // just deletes
            } else {
              this.api.actionExtendedDestroyOnClose(this.refId); //  aborts then deletes
            }
          }
          return noDialog;
        }
      }
    } catch (ex) {
      logger.out.error(`ERROR: _oeScreen.onClose(): ex: %O`, ex);
      return true;
    }
  }

  private updatePopupTitle(refId: string): void {
    let orderId = this.api.getOrderIdForRefId(refId);
    let orderRef = this.api.getOrderReferenceForRefId(refId);

    // display root order id for new or duplicate
    if (
      (orderId === null || orderId.length < 1) &&
      (this.api.isOperationModeNew(refId) || this.api.isOperationModeDuplicate(refId))
    ) {
      orderId = this.api.getOrderIdForRefId(this.rootRefId);
      orderRef = this.api.getOrderReferenceForRefId(this.rootRefId);
    }

    let orderIdTitle: string =
      typeof orderRef === "string" && orderRef.length > 0 ? " - " + orderRef : "";

    let title: string =
      this.utility.determineScreenTitle(this.entryType, this.operationMode) + orderIdTitle;

    if (this.scratchpad) {
      title = this.translate.instant("order_entry.edit.title") + orderIdTitle;
    }

    // // report an error for MODIFY, MODIFY-INFO, AMEND-MARKUP, orderId must exist
    // if((orderId === null || orderId.length < 1) && (this.api.isOperationModeModify(refId) || this.api.isOperationModeModifyInfo(refId) || this.api.isOperationModeAmendMarkup(refId)) ){
    //     logger.out.error(`ERROR: _oeScreen.updatePopupTitle(): Order '${refId}' doesn't have orderId !`);
    // }
    // set the title
    if (typeof title === "string" && title.length > 1) {
      if (this.parentPopout == null) {
        this.parentPopout$.pipe(take(1)).subscribe((parentPopout) => {
          if (parentPopout != null) {
            parentPopout.title = title;
          }
        });
      } else {
        this.parentPopout.title = title;
      }
    }
  }

  /**********************************************************************/
  /* Strategy Panel */
  protected hideStrategyLayout(): void {
    this.control.panels.strategyLayout.show = false;
    // Gamma has it's own strategy view
    if (this.entryType != OENEntryType.GAMMA) {
      this.parentPopout.removeContainerClass("has-strategy");
    }
  }

  protected showStrategyLayout(): void {
    setTimeout(() => {
      // delayed visibility application of strategy panel solves the scrollbar dragging problem on desktop
      this.control.panels.strategyLayout.show = true;
      this.parentPopout.addContainerClass("has-strategy");
      this.updateStrategyPanelSize();
    }, 0);
  }

  public updateStrategyPanelSize(): void {
    if (
      this.panelStrategy instanceof OEStrategyPanelComponent && // Object(this.strategyView)&& this.strategyView.nativeElement == Object(this.strategyView.nativeElement) &&
      this.orderEntryView == Object(this.orderEntryView) &&
      this.orderEntryView.nativeElement == Object(this.orderEntryView.nativeElement)
    ) {
      let height = this.getElementHeight(this.orderEntryView.nativeElement);
      this.panelStrategy.resizeStrategy(height);
    }

    if (
      this.gammaLegViewer instanceof OEGammaViewerComponent &&
      this.orderEntryView == Object(this.orderEntryView) &&
      this.orderEntryView.nativeElement == Object(this.orderEntryView.nativeElement)
    ) {
      let height = this.getElementHeight(this.gammaContainer?.nativeElement);
      this.gammaLegViewer.resizeGammaViewer(height);
    }
  }

  private getElementHeight(element) {
    let clientHeight = element.clientHeight;
    let computedStyle = getComputedStyle(this.orderEntryView.nativeElement);
    let height =
      clientHeight -
      (parseFloat(computedStyle.paddingTop) + parseFloat(computedStyle.paddingBottom));
    return height;
  }

  public executionCalloutChange(event: IEMActionCallout) {
    this.coordinator.cmdExecutionNotification(this.refId, event);
  }
}
